import { z } from "zod";
import {
  TEXT_AGGREGATES,
  compareText,
  fail,
  ok,
  textOperators,
  type FieldKind,
} from "../kind";
import { scalarToText } from "./text";

// Indicatifs des pays qu'on sait compléter depuis un numéro national
// (« 06 12 34 56 78 » en France). Un numéro international (+…) passe
// toujours, quel que soit le pays.
const CALLING_CODES: Record<string, string> = {
  FR: "33", BE: "32", CH: "41", LU: "352", MC: "377", DE: "49", AT: "43",
  ES: "34", PT: "351", IT: "39", NL: "31", GB: "44", IE: "353", DK: "45",
  SE: "46", NO: "47", FI: "358", PL: "48", CZ: "420", GR: "30", RO: "40",
  US: "1", CA: "1", MX: "52", BR: "55", AR: "54", MA: "212", DZ: "213",
  TN: "216", SN: "221", CI: "225", CM: "237", IN: "91", CN: "86", JP: "81",
  AU: "61", IL: "972", AE: "971", TR: "90", ZA: "27",
};

export const phoneSettings = z.object({
  // Pays d'un numéro saisi sans indicatif (ISO 3166-1 alpha-2).
  defaultCountry: z
    .string()
    .refine((code) => code in CALLING_CODES, "Unsupported country.")
    .default("FR"),
  // international : « +33 6 12 34 56 78 » · e164 : « +33612345678 »
  display: z.enum(["international", "e164"]).default("international"),
});

const E164_RE = /^\+[1-9]\d{6,14}$/;

/** Un numéro tel qu'on l'écrit → E.164, ou `null`. */
export function toE164(input: string, defaultCountry: string): string | null {
  const trimmed = input.trim().replace(/^tel:/i, "");
  if (!/^[+\d\s().\-/]+$/.test(trimmed)) return null;
  let digits = trimmed.replace(/[^\d+]/g, "");
  if (digits.startsWith("00")) digits = `+${digits.slice(2)}`;
  if (digits.indexOf("+") > 0) return null;
  if (!digits.startsWith("+")) {
    const code = CALLING_CODES[defaultCountry];
    if (!code) return null;
    // Le 0 de tête national disparaît derrière l'indicatif (sauf en Italie).
    const national = defaultCountry === "IT" ? digits : digits.replace(/^0/, "");
    digits = `+${code}${national}`;
  }
  return E164_RE.test(digits) ? digits : null;
}

/** « +33612345678 » → « +33 6 12 34 56 78 ». */
export function formatInternational(e164: string): string {
  const digits = e164.slice(1);
  const code = Object.values(CALLING_CODES)
    .filter((candidate) => digits.startsWith(candidate))
    .sort((a, b) => b.length - a.length)[0];
  if (!code) return e164;
  const national = digits.slice(code.length);
  // Groupes de deux pour les pays qui écrivent ainsi, de trois sinon.
  const pairs = ["33", "32", "41", "352", "377", "212", "213", "216", "221", "225", "237"].includes(code);
  const head = pairs && national.length % 2 === 1 ? national.slice(0, 1) : "";
  const rest = national.slice(head.length);
  const size = pairs ? 2 : 3;
  const groups: string[] = head ? [head] : [];
  for (let i = 0; i < rest.length; i += size) groups.push(rest.slice(i, i + size));
  return `+${code} ${groups.join(" ")}`;
}

export const phoneKind: FieldKind<"phone"> = {
  type: "phone",
  settings: phoneSettings,
  value: z.string().regex(E164_RE),
  coerce(input, settings) {
    const text = scalarToText(input);
    if (text === null) return fail("Expected a phone number.");
    if (text.trim() === "") return ok(null);
    const e164 = toE164(text, settings.defaultCountry);
    if (!e164) return fail(`"${text}" is not a phone number.`);
    return ok(e164);
  },
  format: (value, settings, ctx) =>
    ctx.style === "plain" || settings.display === "e164" ? value : formatInternational(value),
  compare: (a, b) => compareText(a, b),
  filters: textOperators<"phone">((value) => value),
  aggregates: TEXT_AGGREGATES,
};
